import type { Attributes } from './attributes.js';
import type { Element } from './elements.js';

export const WEAPON_TYPES = [
  'sword',
  'saber',
  'spear',
  'staff',
  'fist',
  'darts',
  'dagger',
  'fan',
  'zither',
  'claw',
] as const;
export type WeaponType = (typeof WEAPON_TYPES)[number];

export const SECT_IDS = [
  'heavenly_king',
  'hidden_sword',
  'tang_clan',
  'five_venoms',
  'emei',
  'jade_mist',
  'beggar',
  'heavenly_demon',
  'wudang',
  'kunlun',
] as const;
export type SectId = (typeof SECT_IDS)[number];

export interface SectDef {
  id: SectId;
  name: string;
  nameZh: string;
  element: Element;
  weapon: WeaponType;
  role: string;
  style: string;
  description: string;
  strengths: string[];
  weaknesses: string[];
  baseAttributes: Attributes;
  /** Attribute gain per level. Fractions accumulate. */
  growth: Attributes;
  colors: { robe: string; trim: string; accent: string; vfx: string };
  /**
   * A sect becomes selectable only once its skill kit is implemented end-to-end
   * (logic, animation, VFX, server validation). See FEATURE_CHECKLIST.md.
   */
  playable: boolean;
  /** Skills in hotbar order (index 0 is the basic attack). */
  skills: string[];
  starterWeapon: string;
  starterArmor: string;
}

export const SECTS: Readonly<Record<SectId, SectDef>> = {
  wudang: {
    id: 'wudang',
    name: 'Wudang',
    nameZh: '武当',
    element: 'earth',
    weapon: 'sword',
    role: 'Balanced swordsman / control',
    style:
      'Circular Taiji swordplay that turns an opponent’s force against them. Wudang disciples trade raw burst for control, sustain and steady pressure.',
    description:
      'High on the cloud-wrapped peaks, the Daoists of Wudang teach that softness overcomes hardness. Their sword arts flow like water around stone, then strike with the weight of the mountain.',
    strengths: ['Crowd control and knockback', 'Reliable sustained damage', 'Sturdy defences'],
    weaknesses: ['Modest single-target burst', 'Qi hungry when spamming techniques'],
    baseAttributes: { str: 6, agi: 5, vit: 7, ene: 6 },
    growth: { str: 1.2, agi: 0.8, vit: 1.3, ene: 1.1 },
    colors: { robe: '#dfe6ee', trim: '#2c3e57', accent: '#d4a54a', vfx: '#f2c96b' },
    playable: true,
    skills: [
      'wudang_sword_combo',
      'wudang_cloud_parting_strike',
      'wudang_sword_qi_crescent',
      'wudang_taiji_formation',
    ],
    starterWeapon: 'wooden_practice_sword',
    starterArmor: 'hempen_robe',
  },
  kunlun: {
    id: 'kunlun',
    name: 'Kunlun',
    nameZh: '昆仑',
    element: 'earth',
    weapon: 'sword',
    role: 'Thunder caster / ranged control',
    style: 'Talismans and sword-chants that call storms down from the snowy peaks.',
    description:
      'Hermits of the western ranges who bind lightning into paper talismans. Their techniques strike from afar and leave foes rooted in place.',
    strengths: ['Long range', 'Area stuns'],
    weaknesses: ['Fragile up close', 'Long cast times'],
    baseAttributes: { str: 4, agi: 5, vit: 5, ene: 9 },
    growth: { str: 0.7, agi: 0.9, vit: 1.0, ene: 1.6 },
    colors: { robe: '#e9e4d4', trim: '#4a3a24', accent: '#8fb8ff', vfx: '#9fd0ff' },
    playable: false,
    skills: [],
    starterWeapon: 'wooden_practice_sword',
    starterArmor: 'hempen_robe',
  },
  heavenly_king: {
    id: 'heavenly_king',
    name: 'Heavenly King',
    nameZh: '天王',
    element: 'metal',
    weapon: 'spear',
    role: 'Vanguard / melee bruiser',
    style: 'Sweeping spear arcs and war cries from a fortress-island stronghold.',
    description:
      'Rebel generals who forged a sect from an island garrison. Heavenly King warriors stand at the front and break enemy lines.',
    strengths: ['Very high health', 'Wide melee sweeps'],
    weaknesses: ['Few ranged options', 'Slow repositioning'],
    baseAttributes: { str: 9, agi: 4, vit: 8, ene: 3 },
    growth: { str: 1.6, agi: 0.6, vit: 1.5, ene: 0.5 },
    colors: { robe: '#7a2626', trim: '#d9b65a', accent: '#f0e0a0', vfx: '#ffe39a' },
    playable: false,
    skills: [],
    starterWeapon: 'wooden_practice_sword',
    starterArmor: 'hempen_robe',
  },
  hidden_sword: {
    id: 'hidden_sword',
    name: 'Hidden Sword',
    nameZh: '藏剑',
    element: 'metal',
    weapon: 'sword',
    role: 'Flying-sword duelist',
    style: 'Concealed blades that fly from the sleeve and return to the scabbard.',
    description:
      'A secretive villa of smiths who temper blades with their own qi. Their disciples command flying swords at blinding speed.',
    strengths: ['Burst damage', 'Mid-range sword control'],
    weaknesses: ['Low defence'],
    baseAttributes: { str: 7, agi: 7, vit: 5, ene: 5 },
    growth: { str: 1.2, agi: 1.2, vit: 0.9, ene: 1.1 },
    colors: { robe: '#e8e0c8', trim: '#a8862d', accent: '#ffffff', vfx: '#fff3c2' },
    playable: false,
    skills: [],
    starterWeapon: 'wooden_practice_sword',
    starterArmor: 'hempen_robe',
  },
  tang_clan: {
    id: 'tang_clan',
    name: 'Tang Clan',
    nameZh: '唐门',
    element: 'wood',
    weapon: 'darts',
    role: 'Hidden-weapon marksman',
    style: 'Needles, darts and mechanical traps thrown from the shadows of Sichuan bamboo.',
    description:
      'A great family of artificers whose hidden weapons are feared across the martial world. They fight from range and punish careless pursuers.',
    strengths: ['Longest range', 'Traps and zone denial'],
    weaknesses: ['Weak when cornered'],
    baseAttributes: { str: 5, agi: 9, vit: 5, ene: 5 },
    growth: { str: 0.9, agi: 1.6, vit: 0.9, ene: 0.9 },
    colors: { robe: '#2f4a33', trim: '#151d16', accent: '#9ccf6a', vfx: '#b6f08a' },
    playable: false,
    skills: [],
    starterWeapon: 'wooden_practice_sword',
    starterArmor: 'hempen_robe',
  },
  five_venoms: {
    id: 'five_venoms',
    name: 'Five Venoms',
    nameZh: '五毒',
    element: 'wood',
    weapon: 'claw',
    role: 'Curse and poison specialist',
    style: 'Spirit-bound serpents, scorpions and centipedes that rot an enemy from within.',
    description:
      'Jungle shamans of the south who keep five sacred beasts. Their poisons stack and spread, wearing down even the hardiest foe.',
    strengths: ['Damage over time', 'Debuffs and slows'],
    weaknesses: ['Low upfront damage'],
    baseAttributes: { str: 5, agi: 6, vit: 6, ene: 7 },
    growth: { str: 0.8, agi: 1.0, vit: 1.1, ene: 1.3 },
    colors: { robe: '#3b2350', trim: '#7fbf3f', accent: '#c2ff6b', vfx: '#93ff4a' },
    playable: false,
    skills: [],
    starterWeapon: 'wooden_practice_sword',
    starterArmor: 'hempen_robe',
  },
  emei: {
    id: 'emei',
    name: 'Emei',
    nameZh: '峨眉',
    element: 'water',
    weapon: 'sword',
    role: 'Healer / support swordswoman',
    style: 'Graceful sword dances and Buddhist sutras that mend allies.',
    description:
      'A nunnery on the misty Sacred Mountain whose disciples heal companions mid-battle and turn aside blades with flowing silk-like steps.',
    strengths: ['Healing and shields', 'Party buffs'],
    weaknesses: ['Lower personal damage'],
    baseAttributes: { str: 5, agi: 6, vit: 6, ene: 8 },
    growth: { str: 0.8, agi: 1.0, vit: 1.0, ene: 1.4 },
    colors: { robe: '#f2f4f8', trim: '#4f7fa8', accent: '#bfe3ff', vfx: '#a8dcff' },
    playable: false,
    skills: [],
    starterWeapon: 'wooden_practice_sword',
    starterArmor: 'hempen_robe',
  },
  jade_mist: {
    id: 'jade_mist',
    name: 'Jade Mist',
    nameZh: '翠烟',
    element: 'water',
    weapon: 'dagger',
    role: 'Ice dancer / skirmisher',
    style: 'Twin moon-blades and freezing fog that blind and chill.',
    description:
      'A sisterhood hidden among jade-green lakes whose dances summon freezing mist. They strike, vanish, and strike again.',
    strengths: ['Mobility', 'Freeze and blind effects'],
    weaknesses: ['Fragile when caught'],
    baseAttributes: { str: 5, agi: 8, vit: 5, ene: 6 },
    growth: { str: 0.9, agi: 1.4, vit: 0.9, ene: 1.1 },
    colors: { robe: '#2f6f6a', trim: '#e6f4f1', accent: '#8fffe8', vfx: '#b8fff2' },
    playable: false,
    skills: [],
    starterWeapon: 'wooden_practice_sword',
    starterArmor: 'hempen_robe',
  },
  beggar: {
    id: 'beggar',
    name: 'Beggar Sect',
    nameZh: '丐帮',
    element: 'fire',
    weapon: 'fist',
    role: 'Brawler / palm master',
    style: 'Dragon palms and staff tricks learned on the roads of the empire.',
    description:
      'The largest brotherhood under heaven, drawn from wanderers of every province. Their blazing palm arts summon roaring dragons of qi.',
    strengths: ['Heavy burst combos', 'Self-sustain'],
    weaknesses: ['Short range'],
    baseAttributes: { str: 8, agi: 6, vit: 6, ene: 4 },
    growth: { str: 1.4, agi: 1.0, vit: 1.1, ene: 0.7 },
    colors: { robe: '#7a5a3a', trim: '#3a2a1a', accent: '#ffb347', vfx: '#ffcf5a' },
    playable: false,
    skills: [],
    starterWeapon: 'wooden_practice_sword',
    starterArmor: 'hempen_robe',
  },
  heavenly_demon: {
    id: 'heavenly_demon',
    name: 'Heavenly Demon',
    nameZh: '天魔',
    element: 'fire',
    weapon: 'saber',
    role: 'Berserker / life-steal',
    style: 'Forbidden blood-fire arts that trade health for overwhelming power.',
    description:
      'Exiles who mastered techniques the orthodox sects forbade. They burn their own vitality for devastating crimson flames.',
    strengths: ['Highest burst', 'Life steal'],
    weaknesses: ['Self-damage', 'Weak defences'],
    baseAttributes: { str: 8, agi: 6, vit: 4, ene: 6 },
    growth: { str: 1.4, agi: 1.0, vit: 0.8, ene: 1.0 },
    colors: { robe: '#1d1416', trim: '#8f1d1d', accent: '#ff3b3b', vfx: '#ff4a2a' },
    playable: false,
    skills: [],
    starterWeapon: 'wooden_practice_sword',
    starterArmor: 'hempen_robe',
  },
};

export function getSect(id: string): SectDef | undefined {
  return (SECTS as Record<string, SectDef>)[id];
}
